import { useCallback, useState } from 'react';

import { useUI } from '@/hooks/ui/useUI';
import { downloadBlob } from '@/utils/blobDownload';
import type { ApiResponse } from '@/api/shared/types';
import type { HybridExportResult, UIExportFormat } from '@/types/fileExport.types';


/** Duración por defecto (ms) del toast de éxito en una descarga síncrona. */
const DEFAULT_SUCCESS_DURATION_MS = 5000;
/** Duración por defecto (ms) del toast informativo cuando el export se encola (ruta 202). */
const DEFAULT_QUEUED_DURATION_MS = 8000;

/** Textos (ya traducidos) que muestra el hook en cada desenlace de la exportación. */
export interface HybridExportMessages {
  /** Error de formato no soportado o fallo de la petición. */
  error: string;
  /** Descarga síncrona completada. */
  success: string;
  /** Export encolado en background (ruta asíncrona 202). */
  queued: string;
}

export interface UseHybridExportConfig {
  /**
   * Ejecuta la petición de exportación. El caller cierra sobre su propio service y payload.
   * Para evitar recrear `runExport` en cada render, conviene memoizarla con `useCallback`.
   */
  requestExport: () => Promise<ApiResponse<HybridExportResult>>;
  /** Textos ya traducidos para cada desenlace. */
  messages: HybridExportMessages;
  /** Duración (ms) del toast de éxito síncrono. Default 5000. */
  successDurationMs?: number;
  /** Duración (ms) del toast de export encolado. Default 8000. */
  queuedDurationMs?: number;
}

export interface UseHybridExportResult {
  /** `true` mientras la exportación está en curso. */
  isExporting: boolean;
  /** Dispara la exportación para el formato indicado (solo se soporta `EXCEL`). */
  runExport: (format: UIExportFormat) => Promise<void>;
}

/**
 * Hook genérico para exportaciones híbridas (patrón sync/async del backend). Es el análogo de
 * `useExport` para endpoints que además de la descarga síncrona (200 + blob) pueden encolar un job
 * en background (202 + jobId). Centraliza el branching que antes estaba triplicado en
 * `useMarksReportExport`, `useAuditExport` e `useIncidentManagementExport`: guarda de formato,
 * estado `isExporting`, manejo de error de la respuesta, descarga del blob en la ruta síncrona y
 * aviso al usuario en la ruta encolada. Cada hook de dominio queda como wrapper delgado que arma
 * su payload, pasa sus textos i18n ya traducidos y reexpone el método con el nombre que espera su
 * componente.
 */
export function useHybridExport(config: UseHybridExportConfig): UseHybridExportResult {
  const {
    requestExport,
    messages,
    successDurationMs = DEFAULT_SUCCESS_DURATION_MS,
    queuedDurationMs = DEFAULT_QUEUED_DURATION_MS,
  } = config;

  const { showApiError, showNotification } = useUI();
  const [isExporting, setIsExporting] = useState(false);

  const runExport = useCallback(async (format: UIExportFormat) => {
    // Los export híbridos actuales solo generan Excel; cualquier otro formato es un error de UI.
    if (format !== 'EXCEL') {
      showNotification('error', messages.error);
      return;
    }

    setIsExporting(true);
    try {
      const response = await requestExport();

      if (!response.success || !response.data) {
        showApiError(response, messages.error);
        return;
      }

      if (response.data.type === 'sync') {
        downloadBlob(response.data.blob, response.data.filename);
        showNotification('success', messages.success, successDurationMs);
      } else {
        // 202: el backend genera el archivo en background (job asíncrono) y notifica al usuario.
        showNotification('info', messages.queued, queuedDurationMs);
      }
    } finally {
      setIsExporting(false);
    }
  }, [
    requestExport,
    messages.error,
    messages.success,
    messages.queued,
    successDurationMs,
    queuedDurationMs,
    showApiError,
    showNotification,
  ]);

  return { isExporting, runExport };
}

export default useHybridExport;
